import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  GoogleMap,
  MarkerF,
  PolygonF,
  CircleF,
  InfoWindowF,
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import styles from "./PropertyMapView.module.css";
import Listing from "../Listing/Listing";
import InfoWindow from "./InfoWindowComponent/InfoWindow";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import IconButton from "@mui/joy/IconButton";

function PropertyMapView({ listings, addressInputState }) {
  const { accessToken } = useAuth();
  const mapRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [infoWindowData, setInfoWindowData] = useState(null);
  const [polygonPaths, setPolygonPaths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [boundaryError, setBoundaryError] = useState(false);
  const [mapCenter, setMapCenter] = useState({
    lat: 28.538336,
    lng: -81.379234,
  });
  const [mapZoom, setMapZoom] = useState(10);
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationButton, setShowLocationButton] = useState(true);

  const markerIcon = useMemo(() => {
    if (typeof window !== "undefined" && window.google) {
      return {
        url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        scaledSize: new window.google.maps.Size(40, 40),
      };
    }
    return null;
  }, []);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported");
      return;
    }
    toast.info("Getting your location...");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userPos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(userPos);
        setMapCenter(userPos);
        setMapZoom(15);
        mapRef.current?.panTo(userPos);
        toast.success("Location found!");
        setShowLocationButton(false);
      },
      () => toast.error("Failed to get location"),
      { enableHighAccuracy: true }
    );
  };

  const fetchCityBoundary = async (location) => {
    setLoading(true);
    setBoundaryError(false);

    try {
      let city = "",
        state = "",
        zipCode = "",
        country = "United States";

      const zipMatch = location.match(/\b\d{5}(-\d{4})?\b/);
      if (zipMatch) zipCode = zipMatch[0];

      const parts = location.split(",").map((p) => p.trim());

      if (zipCode) {
        const beforeZip = parts.find((p) => p.includes(zipCode));
        if (beforeZip) {
          const tokens = beforeZip.split(" ");
          state = tokens[tokens.length - 2] || "";
          city = parts.slice(0, parts.indexOf(beforeZip)).join(", ");
        }
      } else {
        state = parts[parts.length - 2] || "";
        city = parts.slice(0, parts.length - 2).join(", ");
      }

      if (!city && parts.length >= 2) {
        city = parts[0];
        state = parts[1];
      }

      const query = new URLSearchParams({
        ...(zipCode ? { zipCode } : { city, state }),
        country,
      }).toString();

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/boundary?${query}`
      );
      const custData = response.data;

      if (custData.length && custData[0].geojson) {
        const geojson = custData[0].geojson;
        let coordinates = [];

        if (geojson.type === "Polygon") {
          coordinates = [
            geojson.coordinates[0].map(([lng, lat]) => ({
              lat: parseFloat(lat),
              lng: parseFloat(lng),
            })),
          ];
        } else if (geojson.type === "MultiPolygon") {
          coordinates = geojson.coordinates.map((polygon) =>
            polygon[0].map(([lng, lat]) => ({
              lat: parseFloat(lat),
              lng: parseFloat(lng),
            }))
          );
        }

        setPolygonPaths(coordinates);
        setMapCenter({
          lat: parseFloat(custData[0].lat),
          lng: parseFloat(custData[0].lon),
        });
        setMapZoom(12);
      } else {
        setBoundaryError(true);
      }
    } catch (e) {
      console.error("Boundary error:", e);
      setBoundaryError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (addressInputState) fetchCityBoundary(addressInputState);
  }, [addressInputState]);

  const onMapLoad = (map) => {
    mapRef.current = map;
    if (listings?.length) {
      const bounds = new window.google.maps.LatLngBounds();
      listings.forEach(({ latitude, longitude }) => {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          bounds.extend({ lat, lng });
        }
      });
      map.fitBounds(bounds);
      if (map.getZoom() > 10) map.setZoom(10);
    }
  };

  const handleMarkerClick = (listing) => {
    setInfoWindowData(listing);
    setIsOpen(true);
  };

  const handleBookmarkToggle = async (id, isBookmarked) => {
    try {
      const url = `${
        import.meta.env.VITE_BACKEND_URL
      }/listings/favorites/${id}`;
      const config = { headers: { Authorization: accessToken } };
      if (isBookmarked) await axios.delete(url, config);
      else await axios.post(url, {}, config);
    } catch (err) {
      toast.error("Login required to bookmark listings");
    }
  };

  const formatMoney = (number) => {
    if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
    if (number >= 1_000) return `${(number / 1_000).toFixed(1)}K`;
    return number?.toString();
  };

  const handleInfoCardClick = (listing) => {
    if (listing.isMLS) {
      //const fullUrl = `https://mls.foreclosure.com${relativeUrl}`;
      window.open(listing.url, "_blank");
    } else {
      if ("sell_price" in listing && listing.sell_price != null) {
        window.open(`/Buy-Homes/${listing.url}`, "_blank");
      } else if ("rent_price" in listing && listing.rent_price != null) {
        window.open(`/Rent-Homes/${listing.url}`, "_blank");
      } else {
        window.open(`/Buy-Homes/${listing.url}`, "_blank");
      }
    }
  };

  useEffect(() => {
    if (listings.length === 0) {
      setPolygonPaths([]);
      setUserLocation(null);
      setInfoWindowData(null);
      setIsOpen(false);
      setBoundaryError(false);
      setLoading(false);
      setMapCenter({ lat: 28.538336, lng: -81.379234 }); // optional: reset to default
      setMapZoom(10); // optional: reset zoom
    }
  }, [listings]);

  return (
    <div className={styles.propertyMapView}>
      <div className={styles.propertyList}>
        <h2 className={styles.propertyListTitle}>Properties</h2>
        <div className={styles.listingsContainer}>
          {listings.length ? (
            listings.map((l, i) => (
              <div key={i} className={styles.listingItem}>
                <Listing
                  listing={l}
                  handleBookmarkToggle={handleBookmarkToggle}
                />
              </div>
            ))
          ) : (
            <div className={styles.noListings}>
              <p>No properties found</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.mapWrapper}>
        {showLocationButton && (
          <IconButton
            onClick={getUserLocation}
            sx={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
          >
            <MyLocationIcon style={{ fontSize: 28, color: "#3d689c" }} />
          </IconButton>
        )}

        {loading && (
          <div className={styles.mapOverlay}>Loading city boundary...</div>
        )}
        {boundaryError && (
          <div className={styles.mapOverlay}>Error fetching city boundary</div>
        )}

        <GoogleMap
          key={listings.length}
          mapContainerClassName={styles.mapContainer}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          onClick={() => setIsOpen(false)}
          options={{ gestureHandling: "greedy" }}
        >
          {polygonPaths.map((path, i) => (
            <PolygonF
              key={i}
              paths={path}
              options={{
                fillColor: "rgba(0,150,255,0.1)",
                strokeColor: "rgba(0,150,255,0.8)",
                strokeWeight: 2,
              }}
            />
          ))}

          {userLocation && markerIcon && (
            <MarkerF position={userLocation} icon={markerIcon} />
          )}
          {Array.isArray(listings) &&
            listings.length > 0 &&
            listings.map((listing, i) => {
              const lat = parseFloat(listing.latitude);
              const lng = parseFloat(listing.longitude);
              if (isNaN(lat) || isNaN(lng)) return;

              return (
                <React.Fragment key={i}>
                  <MarkerF
                    position={{ lat, lng }}
                    label={{
                      text: listing.isMLS
                        ? listing.sell_price
                        : formatMoney(listing.sell_price || listing.rent_price),
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#000",
                    }}
                    onClick={() => handleMarkerClick(listing)}
                  />
                  {listing.hide_address && (
                    <CircleF
                      center={{ lat, lng }}
                      radius={1000}
                      options={{
                        fillColor: "rgba(0,38,255,0.2)",
                        strokeColor: "rgba(255,0,0,0.5)",
                        strokeWeight: 2,
                      }}
                      onClick={() => handleMarkerClick(listing)}
                    />
                  )}
                </React.Fragment>
              );
            })}

          {isOpen && infoWindowData && (
            <InfoWindowF
              position={{
                lat: +infoWindowData.latitude,
                lng: +infoWindowData.longitude,
              }}
              options={{ disableAutoPan: true }}
              onCloseClick={() => setIsOpen(false)}
            >
              <InfoWindow
                listing={infoWindowData}
                onClick={() => handleInfoCardClick(infoWindowData)}
              />
            </InfoWindowF>
          )}
        </GoogleMap>
      </div>
    </div>
  );
}

export default PropertyMapView;
